<template>
	<!-- Bond condition at a glance: XP beside the name, then stress, then burdens -->
	<div class="pilot-status-list">
		<div
			v-for="pilot in pilots"
			:key="pilot.callsign"
			class="pilot-status"
			@click="openPilot(pilot)"
		>
			<div class="ps-head">
				<img
					:src="pilot.portrait"
					class="ps-portrait"
					@error="e => (e.target.src = './nodata.png')"
				/>
				<div class="ps-ident">
					<span class="ps-sub">{{ pilot.bond ? pilot.bond.name : "No bond" }}</span>
					<span class="ps-callsign">{{ pilot.callsign }}</span>
				</div>
				<!-- XP toward the next bond power (powers cost 8 XP) -->
				<div class="ps-xp" :title="`${pilot.condition.xp} XP`">
					<span class="ps-label">XP</span>
					<span class="ps-pips">
						<span
							v-for="n in XP_PER_POWER"
							:key="n"
							class="ps-pip"
							:class="{ filled: n <= pilot.condition.xp % XP_PER_POWER }"
						></span>
					</span>
					<span class="ps-xp-value">{{ pilot.condition.xp }}</span>
				</div>
			</div>

			<div class="ps-row">
				<span class="ps-label">STRESS</span>
				<span
					class="ps-stress"
					:class="{ critical: pilot.condition.stress >= pilot.condition.stressMax }"
				>
					<span
						v-for="n in pilot.condition.stressMax"
						:key="n"
						class="ps-box"
						:class="{ filled: n <= pilot.condition.stress }"
					></span>
				</span>
				<span class="ps-value"
					>{{ pilot.condition.stress
					}}<span class="ps-of">/{{ pilot.condition.stressMax }}</span></span
				>
			</div>

			<div class="ps-row ps-burdens">
				<span class="ps-label">BURDENS</span>
				<div v-if="pilot.condition.burdens.length" class="ps-burden-list">
					<div v-for="burden in pilot.condition.burdens" :key="burden.name" class="ps-burden">
						<span class="ps-burden-name">{{ burden.name }}</span>
						<span v-if="burden.segments" class="ps-segments">
							<span
								v-for="n in burden.segments"
								:key="n"
								class="ps-segment"
								:class="{ filled: n <= burden.progress }"
							></span>
						</span>
					</div>
				</div>
				<span v-else class="ps-none">None</span>
			</div>
		</div>
	</div>
</template>

<script>
// KTB: bond powers cost 8 XP, so the XP pips show progress toward the next one
const XP_PER_POWER = 8;

export default {
	props: {
		pilots: { type: Array, required: true },
	},
	data() {
		return { XP_PER_POWER };
	},
	methods: {
		openPilot(pilot) {
			this.$router.push({ path: "/pilots", query: { pilot: pilot.callsign } });
		},
	},
};
</script>

<style scoped>
.pilot-status-list {
	height: 100%;
	overflow: auto;
}

.pilot-status {
	display: flex;
	flex-direction: column;
	gap: 0.55em;
	padding: 0.8em 1.1em 0.9em;
	border-bottom: 1px solid rgba(122, 28, 46, 0.6);
	cursor: pointer;
	transition: background-color 0.15s;
}

.pilot-status:hover {
	background: rgba(184, 151, 58, 0.08);
}

/* ── Name line, XP on the right ── */
.ps-head {
	display: flex;
	align-items: center;
	gap: 0.75em;
}

.ps-portrait {
	flex-shrink: 0;
	width: 42px;
	height: 42px;
	object-fit: cover;
	object-position: top;
	border: 1px solid var(--primary-color);
}

.ps-ident {
	flex: 1;
	display: flex;
	flex-direction: column;
	min-width: 0;
}

.ps-sub {
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
	font-family: "Raleway", sans-serif;
	font-size: 10px;
	font-weight: 800;
	letter-spacing: 2px;
	text-transform: uppercase;
	color: #b8973a;
}

.ps-callsign {
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
	font-family: "Big Shoulders Display", cursive;
	font-size: 22px;
	font-weight: 800;
	letter-spacing: 0.08em;
	line-height: 1.1;
	text-transform: uppercase;
	color: var(--text-mission-status);
}

.ps-xp {
	display: flex;
	align-items: center;
	gap: 0.5em;
	flex-shrink: 0;
}

.ps-pips {
	display: flex;
	gap: 3px;
}

.ps-pip {
	width: 7px;
	height: 7px;
	border-radius: 50%;
	border: 1px solid rgba(184, 151, 58, 0.6);
}

.ps-pip.filled {
	background: #b8973a;
	border-color: #b8973a;
}

.ps-xp-value {
	min-width: 1.2em;
	font-family: "Big Shoulders Display", cursive;
	font-size: 18px;
	font-weight: 800;
	text-align: right;
	color: #b8973a;
}

/* ── Stress, then burdens ── */
.ps-label {
	flex: 0 0 auto;
	font-family: "Raleway", sans-serif;
	font-size: 10px;
	font-weight: 800;
	letter-spacing: 2px;
	color: var(--text-location);
}

.ps-row {
	display: flex;
	align-items: center;
	gap: 0.75em;
}

.ps-row > .ps-label {
	width: 6.2em;
}

.ps-stress {
	flex: 1;
	display: flex;
	gap: 4px;
	max-width: 22em;
}

.ps-box {
	flex: 1;
	height: 14px;
	border: 1px solid rgba(209, 200, 184, 0.35);
	background: rgba(26, 26, 31, 0.5);
	transform: skewX(-12deg);
}

.ps-box.filled {
	background: #c0394f;
	border-color: #e0566b;
}

/* At maximum stress the next point breaks them */
.ps-stress.critical .ps-box {
	box-shadow: 0 0 6px rgba(224, 86, 107, 0.9);
}

.ps-value {
	min-width: 2.6em;
	font-family: "Big Shoulders Display", cursive;
	font-size: 18px;
	font-weight: 800;
	color: var(--text-mission-status);
}

.ps-of {
	font-size: 13px;
	color: var(--text-pilot-code);
}

.ps-burdens {
	align-items: flex-start;
}

.ps-burdens > .ps-label {
	padding-top: 3px;
}

.ps-burden-list {
	display: flex;
	flex-direction: column;
	gap: 0.3em;
	min-width: 0;
}

.ps-burden {
	display: flex;
	align-items: center;
	gap: 0.7em;
}

.ps-burden-name {
	font-family: "Titillium Web", sans-serif;
	font-size: 14px;
	text-transform: uppercase;
	color: var(--text-location);
}

.ps-segments {
	display: flex;
	gap: 3px;
}

.ps-segment {
	width: 9px;
	height: 9px;
	transform: rotate(45deg);
	border: 1px solid rgba(184, 151, 58, 0.6);
}

.ps-segment.filled {
	background: #b8973a;
}

.ps-none {
	font-family: "Share Tech Mono", monospace;
	font-size: 12px;
	letter-spacing: 0.1em;
	color: var(--text-pilot-code);
}
</style>
